/**
 * Tear down a standalone `THREE.WebGLRenderer` completely.
 *
 * `renderer.dispose()` releases shader programs but NOT the WebGL context
 * itself. Browsers cap a page at ~16 live contexts, and when the cap is hit
 * they force-lose the OLDEST one — which, in this app, is the main structure
 * viewer. A leaked gallery/browser/thumbnail renderer therefore doesn't fail
 * where it leaked; it blanks the viewer several screens later. (Past the cap,
 * new renderers also throw "Cannot read properties of null (reading
 * 'precision')".)
 *
 * `WEBGL_lose_context` is the supported way to actually hand the context back.
 * Call this from every renderer's cleanup instead of `dispose()` alone.
 *
 * It also unmounts the canvas, so a cleanup never has to reach for the mount
 * node it was appended to. That reach was itself a leak: React detaches refs
 * during the mutation phase but runs `useEffect` cleanups later, so a
 * `if (rendererRef.current && mountRef.current)` guard sees a null mount on a
 * real unmount and skips the teardown entirely. The canvas knows its own
 * parent; ask it.
 */
export function releaseRenderer(renderer) {
  if (!renderer) return;

  renderer.domElement?.parentNode?.removeChild(renderer.domElement);
  renderer.dispose();
  renderer.getContext?.()?.getExtension?.('WEBGL_lose_context')?.loseContext();
}
